<template>
  <ul
    class="menu-list internationalization"
    :class="{ desktop: $isDesktop }"
    data-language-menu-scroll-target
  >
    <li>
      <ui-collapse
        class="language-menu"
        :collapse-id="`collapse-language`"
        @collapse-open="$emit('languageMenuOpened')"
      >
        <template #ui-collapse-icon-start>
          <div>
            <lazy-pu-flag :country-iso="currentLocale.flag" />
          </div>
        </template>
        <template #ui-collapse-title>
          <div>
            <translate tag="em"> Language </translate>
            {{ localeName }}
          </div>
        </template>
        <template #ui-collapse-body>
          <div>
            <language-switch
              v-show="showLanguageOptions"
              data-nosnippet
              @click="closeLanguageSwitch()"
            />
          </div>
        </template>
      </ui-collapse>
    </li>

    <li v-if="showCurrency">
      <currency-switch-menu @click.prevent="closeCurrencySwitch()" />
    </li>
  </ul>
</template>

<script lang="ts">
import { Component, Prop, toNative, Vue } from '~/utility/pu-class-decorator'
import { reportError } from '~/utility/reportError'
import { findLocale } from '../../../../apps/i18n/utils'
import { logger } from '../../../../utility/logger'

@Component({
  emits: ['select', 'languageMenuOpened'],
})
class AppMenuInternationalization extends Vue {
  @Prop()
  totalCounts: number

  @Prop({ default: true })
  showCurrency: boolean

  get showLanguageOptions() {
    if (this.totalCounts === 0) {
      return false
    } else {
      return true
    }
  }

  get currentLocale() {
    const foundLocale = findLocale(this.$i18n.locale, this.$i18n.locales)
    if (!foundLocale) {
      reportError(
        new Error('Coult not find current locale. Using default one'),
        undefined,
        undefined,
        {
          name: 'AppMenuInternationalization',
          context: 'currentLocale',
        },
      )
      return this.$i18n.locales[0]
    }
    return foundLocale
  }

  get localeName(): string {
    return this.currentLocale.name
  }

  closeLanguageSwitch() {
    this.closeSwitch('language-menu')
  }

  closeCurrencySwitch() {
    logger('closeCurrencySwitch')
    this.closeSwitch('currency-menu')
  }

  closeSwitch(className: string) {
    // tslint:disable-next-line semicolon no-extra-semicolon
    ;(
      document.querySelector(`.${className} .ui-collapse-header`) as HTMLElement
    ).click()
    this.$emit('select')
  }
}

export default toNative(AppMenuInternationalization)
</script>

<style scoped>
.internationalization :deep(.list li) {
  padding: 1em 1em 1em 2.2em;
}

.internationalization :deep(.ui-collapse-icon img) {
  height: auto;
  width: 2em;
}

.internationalization :deep(.list) {
  list-style: none;
  padding: 0;
  margin: 0;
  width: 100%;
}

.internationalization :deep(.list li) {
  font-weight: normal;
  color: #333;
  text-decoration: none;
  padding: 1em;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--colour-table-borders);
}

.internationalization :deep(.list li:active) {
  background: rgba(0, 0, 0, 0.4);
}

.internationalization :deep(.list li.selected) {
  background: var(--colour-pale-blue);
}

.internationalization :deep(.ui-collapse) {
  border-top: none;
  border-color: var(--colour-table-borders);
}

.internationalization :deep(.ui-collapse .ui-collapse-content) {
  border-color: var(--colour-table-borders);
  border-bottom: none;
}

.internationalization :deep(.ui-collapse-header) {
  padding: 0.2em 0.75em 0.2em 1.1em;
  min-height: 3em;
}

.internationalization :deep(.ui-collapse-subtitle) {
  font-size: var(--small);
  color: var(--colour-text-light);
}

.internationalization :deep(.ui-collapse-title) {
  font-weight: normal;
}

.internationalization :deep(.ui-collapse-title em) {
  font-weight: normal;
  font-style: italic;
  color: var(--colour-text-light);
  font-size: var(--body);
  display: block;
}

.internationalization :deep(.ui-collapse-content) {
  padding: 0 0 1em 0;
  border-bottom: none;
}

.internationalization :deep(.ui-collapse-icon.start) {
  width: 2.7em;
  max-width: none;
}

.internationalization .language-menu :deep(.ui-collapse-icon.start) {
  text-align: right;
}

.internationalization .language-menu :deep(.list li) {
  padding: 1em;
}

.internationalization :deep(.currency-menu .currency-icon.GBP),
.internationalization :deep(.currency-menu .currency-icon.EUR) {
  font-size: 1.3em;
}

/* desktop & tablet */
.internationalization.desktop {
  gap: 3em;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-width: 50%;
}

.internationalization.desktop li {
  background: white;
  border-radius: 6em;
  position: relative;
  flex-grow: 1;
  min-width: max-content;
}

.internationalization.desktop :deep(.ui-collapse) {
  border: none;
}

.internationalization.desktop :deep(.ui-collapse-content) {
  position: absolute;
  background: white;
  width: auto;
  top: 3em;
  right: 0;
  border: none;
  border-radius: 0.5em;
  overflow: hidden;
  padding: 1em 1.5em;
}

.internationalization.desktop :deep(.ui-collapse-icon.start) {
  width: 3.7em;
}

.internationalization.desktop :deep(.ui-collapse-icon img) {
  height: 2em;
  width: 100%;
}

.internationalization.desktop :deep(.list) {
  display: grid;
  grid-template-columns: max-content max-content;
  background: white;
  z-index: 1;
  position: relative;
  column-gap: 2em;
}

.internationalization.desktop :deep(.currency-menu .currency-icon) {
  font-size: 1.4em;
}

@media screen and (max-width: 1122px) {
  .internationalization.desktop :deep(.ui-collapse-content) {
    left: 0;
    right: auto;
  }

  .internationalization.desktop :deep(.currency-menu .ui-collapse-content) {
    left: -100%;
  }
}

.internationalization.desktop :deep(.locale-flag) {
  flex-grow: 0;
  text-align: left;
}

.internationalization.desktop :deep(.list li) {
  padding: 0.7em 1em;
}

.internationalization.desktop :deep(.language-menu .list li) {
  padding: 0;
}

.internationalization.desktop :deep(.list li:hover) {
  background: rgba(0, 0, 0, 0.2);
  cursor: pointer;
}

.internationalization.desktop :deep(.ui-collapse-icon.start > div) {
  width: auto;
  max-width: 2.5em;
}
</style>
